import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';

interface CreateEntityModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  entityType: 'region' | 'city' | 'vereda' | 'corregimiento';
  onSave: (data: any) => Promise<void>;
  loading?: boolean;
  additionalFields?: React.ReactNode;
  externalFields?: Record<string, any>; // For fields managed externally like countryId
}

export const CreateEntityModal: React.FC<CreateEntityModalProps> = ({
  open,
  onClose,
  title,
  entityType,
  onSave,
  loading = false,
  additionalFields,
  externalFields = {},
}) => {
  const [formData, setFormData] = useState({
    name: '',
    countryId: '',
    regionId: '',
    isActive: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (entityType === 'region' && !externalFields.countryId) {
      newErrors.countryId = 'El país es requerido';
    }

    if (entityType === 'city' && !externalFields.regionId) {
      newErrors.regionId = 'La región es requerida';
    }

    if (entityType === 'vereda' && !externalFields.cityId) {
      newErrors.cityId = 'La ciudad es requerida';
    }

    if (entityType === 'vereda' && !externalFields.corregimientoId) {
      newErrors.corregimientoId = 'El corregimiento es requerido';
    }

    if (entityType === 'corregimiento' && !externalFields.cityId) {
      newErrors.cityId = 'La ciudad es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // Merge form data with external fields
      const dataToSave = { ...formData, ...externalFields };
      console.log('Saving data:', dataToSave);
      await onSave(dataToSave);
      handleClose();
    } catch (error) {
      console.error('Error saving entity:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      countryId: '',
      regionId: '',
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  const getEntityLabel = () => {
    switch (entityType) {
      case 'region':
        return 'departamento';
      case 'city':
        return 'municipio';
      case 'vereda':
        return 'vereda';
      case 'corregimiento':
        return 'corregimiento';
      default:
        return 'entidad';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Crear Nuevo {title}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label={`Nombre del ${getEntityLabel()}`}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            margin="normal"
            required
          />

          {additionalFields}

          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Estado</InputLabel>
              <Select
                value={formData.isActive.toString()}
                onChange={(e) => handleInputChange('isActive', e.target.value === 'true')}
                label="Estado"
              >
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEntityModal;
